import { useState, useEffect, useCallback, useRef } from 'react';
import type { Socket } from 'socket.io-client';
import { getSocket } from '@/lib/socket';

export function useSocket(restaurantId: string | null) {
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!restaurantId) return;

    let mounted = true;

    getSocket(restaurantId)
      .then(s => {
        if (!mounted) return;
        socketRef.current = s;

        const onConnect = () => { if (mounted) setConnected(true); };
        const onDisconnect = () => { if (mounted) setConnected(false); };

        s.on('connect', onConnect);
        s.on('disconnect', onDisconnect);
        if (s.connected) setConnected(true);

        return () => {
          s.off('connect', onConnect);
          s.off('disconnect', onDisconnect);
        };
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        if (msg === 'WS_NO_SESSION') {
          // /api/auth/ws-token returned no token — either user is signed out
          // or the SessionGuard rejected the cookie session. WS falls back
          // to polling; user just sees a slightly delayed live update.
          console.warn(
            '[useSocket] /api/auth/ws-token returned no token — WS will not connect, falling back to polling.',
          );
        } else {
          console.warn('[useSocket] failed to connect:', msg);
        }
      });

    return () => { mounted = false; };
  }, [restaurantId]);

  const on = useCallback(<T = unknown>(event: string, handler: (data: T) => void) => {
    const s = socketRef.current;
    if (!s) return () => {};
    s.on(event, handler as (...args: unknown[]) => void);
    return () => { s.off(event, handler as (...args: unknown[]) => void); };
  }, []);

  return { connected, on };
}
